import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PATH } from '../endpoints';
import { buildLogoutUrl } from '../keycloak';

/**
 * Configuration interface for the useSessionTimeout hook.
 */
interface SessionTimeoutConfig {
  inactivityLimit: number;
  forcedLogoutLimit: number;
  storage?: Storage;
}

const useSessionTimeout = ({
  inactivityLimit,
  forcedLogoutLimit,
  storage = sessionStorage,
}: SessionTimeoutConfig) => {
  const inactivityTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const forcedLogoutTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();

  /**
   * Handles session logout by clearing session data and redirecting to the logout URL.
   */
  const handleLogoutSession = useCallback(() => {
    const idToken = storage.getItem('id_token');
    const redirectUri = window.location.origin 

    // Always clear storage
    storage.removeItem('access_token');
    storage.removeItem('refresh_token');
    storage.removeItem('id_token');
    storage.removeItem('pkceCodeVerifier');

    if (idToken) {
      // Redirect to Keycloak logout
      const logoutUrl = buildLogoutUrl(redirectUri, idToken);
      if (logoutUrl) {
        window.location.href = logoutUrl;
        return;
      }
    }

    // Fallback: just navigate inside app
    navigate(PATH, { replace: true });
  }, [storage, navigate, buildLogoutUrl]);

  /**
   * Resets the inactivity timer
   */
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
    }
    inactivityTimeoutRef.current = setTimeout(() => {
      handleLogoutSession();
    }, inactivityLimit);
  }, [handleLogoutSession, inactivityLimit]);

  /**
   * Sets up forced logout timer
   */
  const setupForcedLogoutTimer = useCallback(() => {
    if (forcedLogoutTimeoutRef.current) {
      clearTimeout(forcedLogoutTimeoutRef.current);
    }
    forcedLogoutTimeoutRef.current = setTimeout(() => {
      handleLogoutSession();
    }, forcedLogoutLimit);
  }, [handleLogoutSession, forcedLogoutLimit]);

  /**
   * Sets up user activity listeners
   */
  const setupActivityListeners = useCallback(() => {
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach((event) => {
      window.addEventListener(event, resetInactivityTimer);
    });
    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, resetInactivityTimer);
      });
    };
  }, [resetInactivityTimer]);

  /**
   * Init on mount
   */
  useEffect(() => {
    resetInactivityTimer();
    setupForcedLogoutTimer();
    const cleanupActivityListeners = setupActivityListeners();

    return () => {
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
      }
      if (forcedLogoutTimeoutRef.current) {
        clearTimeout(forcedLogoutTimeoutRef.current);
      }
      cleanupActivityListeners();
    };
  }, [resetInactivityTimer, setupActivityListeners, setupForcedLogoutTimer]);
};

export default useSessionTimeout;
